import './account.css';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api';
import { useSession } from '../../auth/session';
import { LegalLinks } from '../../components/LegalLinks';
import { LangSwitch, t, tn } from '../../i18n';
import { useConfig } from '../../lib/config';
import { useLive } from '../../lib/live';
import type { Me, MeClient } from '../../types';

// Wartebildschirm, solange keine Firma freigeschaltet ist: „In Prüfung“ oder „nicht freigegeben“.
// Wird die Firma freigegeben, während der Bildschirm offen ist, erscheint „freigeschaltet“ mit „Zur App“.
export function RegistrationStatus({ me }: { me: Me }) {
  const { refresh, logout } = useSession();
  const navigate = useNavigate();
  const config = useConfig();
  const [approved, setApproved] = useState<MeClient | null>(null);

  useEffect(() => {
    document.title = `${t('Registrierung')} · Elbcons`;
  }, []);

  // Live: Freigabe, Ablehnung oder „wieder geöffnet“ durch die Kanzlei
  useLive(['clients'], () => {
    void api
      .get<Me>('/api/auth/me')
      .then((m) => {
        const active = m.clients.find((c) => c.status === 'active');
        if (active) setApproved(active);
        else void refresh();
      })
      .catch(() => {});
  });

  // Die zuletzt angemeldete Firma bestimmt den Status
  const firm = approved ?? me.clients[me.clients.length - 1];
  const status = approved ? 'active' : (firm?.status ?? 'pending');

  async function signOut() {
    await logout();
    navigate('/login', { replace: true });
  }

  return (
    <div className="mapp">
      <div className="regwrap">
        <div className="in">
          <div className="brandrow">
            <span className="logo wm" role="img" aria-label="Elbcons" />
            <span style={{ marginInlineStart: 'auto' }}>
              <LangSwitch />
            </span>
          </div>
          <h1>
            {status === 'active'
              ? t('Ihr Zugang ist freigeschaltet')
              : status === 'rejected'
                ? t('Registrierung nicht freigegeben')
                : t('Danke, wir prüfen Ihre Angaben')}
          </h1>
          <div className="card regcard" role="status">
            {status === 'active' && firm ? (
              <>
                <p>
                  <span className="badge b-ok">{t('freigeschaltet')}</span>
                </p>
                <p>
                  {/* {name} endet mit dem Punkt (bei „… e.K.“ keiner mehr dazu) */}
                  {tn(
                    'Willkommen bei Elbcons, {name} Ihre Mandantennummer ist {number}. Sie können sich jetzt mit Ihrer E-Mail und Ihrem Passwort anmelden.',
                    { name: /\.$/.test(firm.name) ? firm.name : `${firm.name}.`, number: <b>{firm.number}</b> },
                  )}
                </p>
              </>
            ) : status === 'rejected' && firm ? (
              <>
                {firm.rejectReason && <p>{t('Hinweis Ihrer Kanzlei: „{reason}“', { reason: firm.rejectReason })}</p>}
                <p>{t('Ihre Kanzlei meldet sich bei Ihnen.')}</p>
              </>
            ) : (
              <>
                <p>
                  <span className="badge b-warn">{t('In Prüfung')}</span>
                </p>
                <p>
                  {config.mailEnabled
                    ? t(
                        'Ihre Kanzlei prüft Ihre Daten. Sobald Ihr Zugang freigeschaltet ist, bekommen Sie eine E-Mail an {email}.',
                        { email: me.user.email },
                      )
                    : t(
                        'Ihre Kanzlei prüft Ihre Daten. Sobald Ihr Zugang freigeschaltet ist, sehen Sie es hier und können die App nutzen.',
                      )}
                </p>
              </>
            )}
          </div>
          {status === 'active' ? (
            <button type="button" className="btn2 dark" style={{ marginTop: 12, width: '100%' }} onClick={() => void refresh()}>
              {t('Zur App')}
            </button>
          ) : (
            <div className="actions-row">
              <button type="button" className="btn2" onClick={() => void signOut()}>
                {t('Abmelden')}
              </button>
            </div>
          )}
          <LegalLinks />
        </div>
      </div>
    </div>
  );
}
